import cardPainterInstanceCache from '../../../Widgets/DataPainterWidget/Core/CardPainterInstanceCache';
import jxWindowCache from '../../../JxControls/JxWindowManager/JxWindowCache';
import jxWindowManager from '../../../JxControls/JxWindowManager/JxWindowManager';
import { WindowDisplayStates } from '../../../JxControls/JxWindowManager/Types/WindowDisplayStates';
import {
    CMD_WORKSPACE_CTRL_REGISTRY_KEY,
    META_TYPE as WSC_META_TYPE,
    META_VER as WSC_META_VER,
    WSC_TARGET,
} from '../YourLibraryTab/CommandBarWidget/Commands/workspaceCtrlCommandRegisteryType';

/**
 * Builds workspace snapshots from the open cards (incl. geo), and re-applies per-card
 * geo + window state (pin, minimized) after a workspace load.
 */
export default class WorkspaceSnapshotBuilder {
    /**
     * @param {string} rk
     * @param {string} name
     * @returns {{rk: string, name: string, cards: object[]}}
     */
    static build(rk, name) {
        const cards = cardPainterInstanceCache
            .getAll()
            .map((inst) => WorkspaceSnapshotBuilder._toCard(inst))
            .sort((a, b) => WorkspaceSnapshotBuilder._cardId(a).localeCompare(WorkspaceSnapshotBuilder._cardId(b)));
        return { rk, name, cards };
    }

    /**
     * Empty workspace, only the Workspaces card so there is always a way to manage/switch.
     * @param {string} rk
     * @param {string} name
     * @returns {{rk: string, name: string, cards: object[]}}
     */
    static blank(rk, name) {
        const ctrl = {
            registryKey: CMD_WORKSPACE_CTRL_REGISTRY_KEY,
            meta: { type: WSC_META_TYPE, ver: WSC_META_VER, target: WSC_TARGET },
            props: {},
            isPinned: false,
            displayState: WindowDisplayStates.Normal,
        };
        return { rk, name, cards: [ctrl] };
    }

    /**
     * @param {object|null} a
     * @param {object|null} b
     * @returns {boolean} true when name and cards are equal
     */
    static isSame(a, b) {
        if (!a || !b) return false;
        return a.name === b.name && JSON.stringify(a.cards) === JSON.stringify(b.cards);
    }

    /**
     * Puts the snapshot geo into jxWindowCache, call before the cards mount so getPos() finds it.
     * @param {object|null} snapshot
     */
    static applyGeo(snapshot) {
        const cards = snapshot?.cards;
        if (!Array.isArray(cards)) return;
        cards.forEach((card) => WorkspaceSnapshotBuilder._applyCardGeo(card));
    }

    /**
     * Applies pin + minimized state from the snapshot onto jxWindowCache. Skips cards without
     * a cached geo, so a fresh entry never ends up with a 0/0 geo.
     * @param {object|null} snapshot
     */
    static applyWindowStates(snapshot) {
        (snapshot?.cards ?? []).forEach((card) => {
            const id = WorkspaceSnapshotBuilder._cardId(card);
            const geo = jxWindowCache.getByWindowId(id);
            if (!geo) return;

            jxWindowCache.setByWindowId(id, { isPinned: card.isPinned });
            if (card.displayState === WindowDisplayStates.Minimized) jxWindowManager.minimizeWindow(id);
            else jxWindowCache.setByWindowId(id, { displayState: WindowDisplayStates.Normal, restoreGeo: null, targetAnchor: null });
        });
    }

    static _toCard(inst) {
        const geo = jxWindowCache.getByWindowId(inst.rk);
        return {
            registryKey: inst.registryKey,
            meta: inst.meta,
            props: inst.props ?? {},
            isPinned: geo?.isPinned ?? false,
            displayState: geo?.displayState ?? WindowDisplayStates.Normal,
            geo: WorkspaceSnapshotBuilder._toGeo(geo),
        };
    }

    static _toGeo(geo) {
        if (!geo) return null;
        return { posX: geo.posX, posY: geo.posY, width: geo.width, height: geo.height };
    }

    static _applyCardGeo(card) {
        if (!card.geo) return;
        jxWindowCache.setByWindowId(WorkspaceSnapshotBuilder._cardId(card), card.geo);
    }

    // same id as CardPainterManager: `${meta.type}:${meta.target}`
    static _cardId(card) {
        return `${card.meta.type}:${card.meta.target}`;
    }
}
